import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { Link } from "react-router-dom";
import { Plus, Storefront, QrCode, ChartBar, Gift, ArrowRight, FileImage, Crown } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.get("/outlets");
      setOutlets(data);
    } catch (e) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (error) {
    return (
      <DashboardLayout title="Outlets">
        <div className="card-pop p-10 text-center" style={{ background: "#FFE5E2" }}>
          <h3 className="display text-xl font-black mb-2">Failed to load data</h3>
          <p className="text-[var(--ink-muted)] mb-4">We couldn't fetch your outlets. Please try again.</p>
          <button onClick={load} className="btn-primary">
            Retry
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Outlets"
      actions={
        <Link to="/outlet/new" data-testid="dashboard-new-outlet-btn" className="btn-primary">
          <Plus size={18} weight="bold" /> New outlet
        </Link>
      }
    >
      {user?.is_admin && (
        <Link to="/admin" className="block mb-6">
          <div className="card-pop p-4 flex items-center gap-4 bg-gradient-to-r from-orange-50 to-white border-orange-200 hover:border-orange-400 transition-colors">
            <div className="h-10 w-10 rounded-lg bg-orange-500 text-white flex items-center justify-center shadow-md">
              <Crown size={20} weight="fill" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-sm">Super Admin Portal</div>
              <div className="text-xs text-[var(--ink-muted)]">Manage owners, outlets, analytics, and platform settings</div>
            </div>
            <ArrowRight size={16} className="text-orange-500" />
          </div>
        </Link>
      )}
      {loading ? (
        <div className="text-[var(--ink-muted)]">Loading…</div>
      ) : outlets.length === 0 ? (
        <div className="card-pop p-10 text-center" style={{ background: "var(--secondary)" }}>
          <div className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center bg-white border-[1.5px] border-[var(--ink)]">
            <Storefront size={28} weight="bold" />
          </div>
          <h3 className="display mt-4 text-2xl font-black">Create your first outlet</h3>
          <p className="text-[var(--ink-muted)] mt-2 max-w-sm mx-auto">
            Add your business name and Google review link. We'll generate a QR code in seconds.
          </p>
          <Link to="/outlet/new" data-testid="dashboard-empty-create-btn" className="btn-primary mt-6 inline-flex">
            <Plus size={18} weight="bold" /> New outlet
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {outlets.map((o) => (
            <div key={o.id} data-testid={`outlet-card-${o.slug}`} className="card-pop p-6" style={{ background: "#fff" }}>
              <div className="flex items-start justify-between">
                <div>
                  <span className="tag" style={{ background: "var(--pastel-yellow)" }}>
                    <Storefront size={12} weight="bold" /> Outlet
                  </span>
                  <h3 className="display text-xl font-extrabold mt-3">{o.name}</h3>
                  <div className="text-xs text-[var(--ink-muted)] mt-1 truncate max-w-[230px]">/r/{o.slug}</div>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <Link to={`/outlet/${o.id}`} data-testid={`outlet-${o.slug}-edit-link`} className="text-xs font-bold py-2 px-3 rounded-lg border border-[var(--ink)] hover:bg-[var(--secondary)]">
                  Configure
                </Link>
                <Link to={`/outlet/${o.id}/qr`} data-testid={`outlet-${o.slug}-qr-link`} className="text-xs font-bold py-2 px-3 rounded-lg border border-[var(--ink)] hover:bg-[var(--pastel-blue)] inline-flex items-center gap-1">
                  <QrCode size={14} weight="bold" /> QR
                </Link>
                <Link to={`/outlet/${o.id}/poster`} data-testid={`outlet-${o.slug}-poster-link`} className="text-xs font-bold py-2 px-3 rounded-lg border border-[var(--ink)] hover:bg-[var(--pastel-yellow)] inline-flex items-center gap-1">
                  <FileImage size={14} weight="bold" /> Poster
                </Link>
                <Link to={`/outlet/${o.id}/analytics`} data-testid={`outlet-${o.slug}-analytics-link`} className="text-xs font-bold py-2 px-3 rounded-lg border border-[var(--ink)] hover:bg-[var(--pastel-purple)] inline-flex items-center gap-1">
                  <ChartBar size={14} weight="bold" /> Stats
                </Link>
                <Link to={`/outlet/${o.id}/redeem`} data-testid={`outlet-${o.slug}-redeem-link`} className="text-xs font-bold py-2 px-3 rounded-lg border border-[var(--ink)] hover:bg-[var(--secondary)] inline-flex items-center gap-1">
                  <Gift size={14} weight="bold" /> Redeem
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
